import { ArrowRight } from "lucide-react";
import { gohighlevelConfig as defaultConfig } from "./service-platform-config";
import type { PlatformConfig } from "./service-platform-config";

export function ServicePainAndProof({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  const symptoms = config.symptoms;
  return (
    <>
      {/* §2 PAIN DIAGNOSIS — The Symptoms of a System Built Wrong */}
      <section className="relative overflow-hidden py-16 md:py-24 px-5 sm:px-8 lg:px-12 revops-symptoms-section">
        {/* Layer 0: Base white-to-pale-blue subtle gradient */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background:
              "linear-gradient(180deg, #FFFFFF 0%, #F8FCFF 36%, #F2F8FF 72%, #EBF5FF 100%)",
          }}
        />

        {/* Layer 1: Soft cyan/blue radial atmosphere centered across the card region */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 65% 55% at 50% 52%, rgba(79, 190, 255, 0.09) 0%, rgba(79, 190, 255, 0.03) 48%, transparent 75%),
              radial-gradient(circle at 12% 48%, rgba(60, 180, 255, 0.08) 0%, transparent 55%),
              radial-gradient(circle at 88% 22%, rgba(47, 165, 255, 0.08) 0%, transparent 52%)
            `,
          }}
        />

        {/* Layer 2: Architectural sweeping pale-blue curves matching the reference */}
        {/* SVG Architectural Wave 1: Upper-Right sweeping wave */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute -top-10 -right-16 w-[420px] sm:w-[580px] lg:w-[740px] h-auto z-[2] select-none opacity-85"
          viewBox="0 0 740 680"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M740 0C610 80 470 210 440 380C410 550 510 650 560 680H740V0Z"
            fill="url(#wave-ur-grad-1)"
          />
          <path
            d="M740 0C560 140 390 280 370 450C350 620 450 670 480 680H740V0Z"
            fill="url(#wave-ur-grad-2)"
          />
          <path
            d="M740 0C560 140 390 280 370 450C350 620 450 670 480 680"
            stroke="rgba(11, 124, 242, 0.12)"
            strokeWidth="1.5"
            fill="none"
          />
          <defs>
            <linearGradient
              id="wave-ur-grad-1"
              x1="740"
              y1="0"
              x2="440"
              y2="680"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#D9EEFC" stopOpacity="0.65" />
              <stop offset="0.6" stopColor="#E8F4FD" stopOpacity="0.3" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="wave-ur-grad-2"
              x1="740"
              y1="0"
              x2="360"
              y2="680"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#CDE7FB" stopOpacity="0.45" />
              <stop offset="0.7" stopColor="#E3F2FD" stopOpacity="0.15" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* SVG Architectural Wave 2: Upper-Left & Mid-Left sweeping curve */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute top-4 -left-20 w-[380px] sm:w-[500px] lg:w-[640px] h-auto z-[2] select-none opacity-80"
          viewBox="0 0 640 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0C120 70 230 180 250 320C270 460 190 560 140 600H0V0Z"
            fill="url(#wave-ul-grad)"
          />
          <path
            d="M0 0C120 70 230 180 250 320C270 460 190 560 140 600"
            stroke="rgba(11, 124, 242, 0.1)"
            strokeWidth="1.5"
            fill="none"
          />
          <defs>
            <linearGradient
              id="wave-ul-grad"
              x1="0"
              y1="0"
              x2="260"
              y2="600"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#D9EEFC" stopOpacity="0.55" />
              <stop offset="0.7" stopColor="#EEF7FE" stopOpacity="0.2" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* SVG Architectural Wave 3: Lower-Left broad sweeping curve */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-10 -left-12 w-[460px] sm:w-[620px] lg:w-[780px] h-auto z-[2] select-none opacity-85"
          viewBox="0 0 780 440"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 440C160 380 320 320 480 330C640 340 730 400 780 440H0Z"
            fill="url(#wave-ll-grad)"
          />
          <path
            d="M0 400C220 320 440 270 650 310C720 324 760 350 780 370"
            stroke="rgba(11, 124, 242, 0.12)"
            strokeWidth="1.5"
            fill="none"
          />
          <defs>
            <linearGradient
              id="wave-ll-grad"
              x1="0"
              y1="330"
              x2="780"
              y2="440"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#D7ECFB" stopOpacity="0.55" />
              <stop offset="0.6" stopColor="#EAF5FD" stopOpacity="0.25" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* SVG Architectural Wave 4: Subtle Lower-Right accent curve */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-8 -right-10 w-[360px] sm:w-[480px] lg:w-[580px] h-auto z-[2] select-none opacity-75"
          viewBox="0 0 580 380"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M580 380C460 320 340 280 200 300C120 312 40 350 0 380H580Z"
            fill="url(#wave-lr-grad)"
          />
          <path
            d="M580 340C430 270 290 250 140 280C70 295 20 320 0 340"
            stroke="rgba(11, 124, 242, 0.08)"
            strokeWidth="1.2"
            fill="none"
          />
          <defs>
            <linearGradient
              id="wave-lr-grad"
              x1="580"
              y1="280"
              x2="0"
              y2="380"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#DFEFFB" stopOpacity="0.5" />
              <stop offset="0.7" stopColor="#EFF7FD" stopOpacity="0.15" />
              <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        <div className="relative z-[3] mx-auto max-w-[1440px]">
          {/* Header */}
          <div className="mb-12 md:mb-14 text-center">
            <div
              className="inline-flex items-center gap-2 mb-3 font-sans font-bold uppercase tracking-[0.22em]"
              style={{
                fontSize: "11px",
                color: "#0B7CF2",
              }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: "#27DC5D" }}
              />
              {config.painEyebrow}
            </div>
            <h2
              className="font-display font-extrabold"
              style={{
                fontSize: "clamp(46px, 5.6vw, 84px)",
                lineHeight: 0.94,
                letterSpacing: "-0.05em",
                color: "#071E61",
              }}
            >
              <span className="block">{config.painHeadlineNavy}</span>
              <span className="block" style={{ color: "#0B7CF2" }}>
                {config.painHeadlineBlue}
              </span>
            </h2>
            <p
              className="mx-auto mt-5 max-w-[820px] text-balance"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "18px",
                lineHeight: 1.5,
                color: "#536B89",
              }}
            >
              {config.painSub}
            </p>
          </div>

          {/* 3x2 Grid of Diagnostic Symptom Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {symptoms.map((s, i) => {
              const numStr = String(i + 1).padStart(2, "0");
              const isCardSix = i === 5;
              const IconComponent = s.icon;

              return (
                <div
                  key={s.title}
                  className={`revops-symptom-card group relative overflow-hidden rounded-[26px] p-7 md:p-8 transition-all duration-300 ${
                    isCardSix
                      ? "revops-symptom-card-bridge"
                      : "revops-symptom-card-standard"
                  }`}
                  style={{
                    background: isCardSix
                      ? "linear-gradient(135deg, #FFFFFF 0%, #EFF8FF 100%)"
                      : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,252,255,0.95) 100%)",
                    border: isCardSix
                      ? "1.5px solid rgba(11,124,242,0.32)"
                      : "1px solid rgba(8,46,103,0.07)",
                    boxShadow: isCardSix
                      ? "0 14px 40px rgba(11,124,242,0.08), 0 2px 6px rgba(8,46,103,0.03)"
                      : "0 12px 36px rgba(8,46,103,0.05), 0 2px 6px rgba(8,46,103,0.025)",
                    minHeight: "190px",
                  }}
                >
                  {/* Red (or Electric Blue on Card 06) Top Accent Bar */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 left-7 sm:left-8 rounded-full"
                    style={{
                      width: "74px",
                      height: "3.5px",
                      background: isCardSix
                        ? "linear-gradient(90deg, #19A5FF, #0B7CF2)"
                        : "linear-gradient(90deg, #FF3F46, #FF5A61)",
                    }}
                  />

                  {/* Large background watermark number in upper-right */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-2.5 right-6 select-none font-display font-black leading-none"
                    style={{
                      fontSize: "clamp(74px, 5.6vw, 92px)",
                      color: "rgba(255,63,70,0.11)",
                      letterSpacing: "-0.06em",
                      lineHeight: 0.88,
                    }}
                  >
                    {numStr}
                  </div>

                  {/* Micro-label: ● SYMPTOM 0X */}
                  <div
                    className="relative z-10 flex items-center gap-2 mb-4 font-sans font-bold uppercase tracking-[0.14em]"
                    style={{
                      fontSize: "11px",
                      color: "#FF3F46",
                    }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "#FF3F46" }}
                    />
                    SYMPTOM {numStr}
                  </div>

                  {/* Main card body: icon + title and description */}
                  <div className="relative z-10 flex items-start gap-4">
                    <div
                      className="rounded-[16px] flex items-center justify-center shrink-0"
                      style={{
                        background: "#FFF1F2",
                        width: 52,
                        height: 52,
                      }}
                    >
                      <IconComponent
                        className="w-6 h-6"
                        style={{ color: "#FF3F46" }}
                      />
                    </div>

                    <div className="flex-1 min-w-0 pr-6">
                      <h3
                        className="font-display font-bold mb-2"
                        style={{
                          fontSize: "clamp(19px, 1.4vw, 22.5px)",
                          lineHeight: 1.15,
                          letterSpacing: "-0.025em",
                          color: isCardSix ? "#0B7CF2" : "#071E61",
                        }}
                      >
                        {s.title}
                      </h3>
                      <p
                        className="line-clamp-3"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "14.5px",
                          lineHeight: 1.5,
                          color: "#536B89",
                        }}
                      >
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Section CTA inside the section */}
          <div className="relative z-10 flex flex-col items-center justify-center text-center mt-12 md:mt-16">
            <a
              href="/book"
              className="group inline-flex items-center justify-center gap-2 rounded-full font-sans font-bold text-white transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48F45A]"
              style={{
                background: "linear-gradient(135deg, #2BC845, #48F45A)",
                height: 52,
                padding: "0 32px",
                fontSize: "15px",
                boxShadow: "0 10px 24px rgba(72,244,90,0.30)",
              }}
            >
              <span>{config.painCtaLabel}</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <p
              className="font-sans text-center mt-3 max-w-[620px]"
              style={{
                fontSize: "14px",
                lineHeight: 1.45,
                color: "#536B89",
                fontWeight: 500,
              }}
            >
              {config.painCtaCopy}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
